import React, {
  createRef,
  FunctionComponent,
  HTMLAttributes,
  useEffect,
  useState,
} from 'react';
import {
  CardElement,
  useCheckoutPricing,
  useRecurly,
} from '@recurly/react-recurly';
import {
  Currency,
  getCurrencyFromCountryCode,
  PaymentFormContainer,
  PaymentFormFields,
} from '@focusritegroup/web-ui';
import {
  PaymentFormFieldID,
  CountryCode,
  User as PaymentUser,
} from '@focusritegroup/web-ui/dist/types/components/PaymentForm/PaymentForm.models';
import {User} from '@focusrite-novation/ampify-api';
import {
  createCardSubscriptionRequestBody,
  createCouponSubscriptionRequestBody,
  getPrice,
  RecurlyToken,
  SubscriptionRequestBody,
} from './PaymentFormRecurlyContainer.utils';
import styled, {css} from 'styled-components';
import {RecurlyError} from '@recurly/recurly-js';
import Typography from '../../constants/typography';
import Colours from '../../constants/colours';

interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  hideCardInput?: boolean;
}
const Container = styled.div<ContainerProps>(
  ({hideCardInput}) => css`
    width: 100%;

    .recurly-element-card {
      ${hideCardInput &&
      css`
        pointer-events: none;
        background: #f2f2f2;
      `}
    }
  `
);

interface PaymentFormRecurlyContainerProps {
  /** User info is used to auto populate the form and the id is passed back when submitting */
  user: User;
  /** Countries that the user is allowed to purchase from */
  supportedCountries: string[];
  /** Set generic error message */
  error?: string;
  /** Form submission success message, displayed at the top of the form */
  successMessage?: string;
  /** Submit button text */
  submitButtonText: string;
  /** create subscription, triggered on submit */
  subscribe: (requestBody: SubscriptionRequestBody) => Promise<any>;
  /** Use to pre-populate the form with values */
  initialPaymentFormFieldValues?: Partial<PaymentFormFields>;
  /** Breakpoint at which the smaller collapsed version should be displayed */
  collapsedBreakpoint?: string;
  /** The id of the selected plan */
  planId: string;
  /** choose to use the update billing form */
  useUpdateBillingForm: boolean;
  /** The billing duration, usually something like 'month', 'year' etc */
  billingDuration: string;
}

export const PaymentFormRecurlyContainer: FunctionComponent<PaymentFormRecurlyContainerProps> = ({
  subscribe,
  error,
  user,
  supportedCountries,
  ...props
}) => {
  const recurly = useRecurly();
  const form = createRef<HTMLFormElement>();
  const [
    recurlyPricingError,
    setRecurlyPricingError,
  ] = useState<RecurlyError>();
  const [priceState, setPricing] = useCheckoutPricing(
    {
      currency: getCurrencyFromCountryCode(user.country as CountryCode),
    },
    setRecurlyPricingError
  );

  const [errorMessage, setErrorMessage] = useState('');

  const requestRecurlyToken = (form: HTMLFormElement) =>
    new Promise<string>((resolve, reject) => {
      recurly.token(form, (error: any, token: RecurlyToken) =>
        error ? reject(error) : resolve(token.id)
      );
    });

  const [hide, setHide] = useState<Partial<PaymentFormFields<boolean>>>({
    couponCode: true,
  });
  const [useCoupon, setUseCoupon] = useState<boolean>(false);

  const handleToggle = (fieldID: PaymentFormFieldID, isOpen: boolean) => {};

  const handleOnSubmit = async (
    values: PaymentFormFields,
    user: User,
    currency: Currency
  ) => {
    let token: string;
    try {
      // @ts-ignore - should not be possible for form.current to be undefined
      token = await requestRecurlyToken(form.current);
      return subscribe(
        createCardSubscriptionRequestBody(
          values,
          props.planId,
          user,
          currency,
          token
        )
      );
    } catch (error) {
      setErrorMessage(
        error.message
          ? error.message
          : 'Your card details appear to be incorrect. Please try again.'
      );
    }
  };

  const handlePricingChanged = (
    values: PaymentFormFields,
    currency: Currency
  ) => {
    setRecurlyPricingError(undefined);

    const shippingAddress = {
      first_name: values.firstName,
      last_name: values.lastName,
      country: values.country,
      postal_code: values.postalCode,
    };

    setRecurlyPricingError(undefined);
    setPricing({
      subscriptions: [
        {
          plan: props.planId,
          quantity: 1,
        },
      ],
      shippingAddress,
      adjustments: [],
      currency,
      coupon: values.couponCode.length > 0 ? values.couponCode : '',
    });
  };

  useEffect(() => {
    handlePricingChanged(
      {
        email: user.email as string,
        country: user.country as CountryCode,
        firstName: user.firstName as string,
        lastName: user.lastName as string,
        postalCode: '',
        street: '',
        street2: '',
        region: '',
        city: '',
        couponCode: '',
      },
      getCurrencyFromCountryCode(user.country as CountryCode)
    );
  }, []);

  useEffect(() => {
    const hasPlan = user.plans?.includes(props.planId);
    if (hasPlan && !props.useUpdateBillingForm) {
      setErrorMessage('You have already subscribed to this plan');
    }
  }, []);

  return (
    <Container hideCardInput={false} data-testid="payment-form">
      <PaymentFormContainer
        form={form}
        onSubmit={handleOnSubmit}
        renderCardInput={() => <CardElement />}
        supportedCountries={supportedCountries}
        onPricingChanged={handlePricingChanged}
        price={getPrice(priceState, useCoupon, props.billingDuration)}
        user={user as PaymentUser}
        error={error ? error : errorMessage}
        couponError={recurlyPricingError}
        handleToggle={handleToggle}
        collapsedBreakpoint="600px"
        hide={{couponCode: true}}
        submitButtonStyle={{
          width: '100%',
          textAlign: 'center',
          background: Colours.green,
          fontSize: Typography.button.fontSize,
        }}
        errorShouldReplaceLabel={true}
        {...props}
      />
    </Container>
  );
};
